import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { purchaseService, purchaseReturnService } from '../../utils/apiService';

const PurchaseReturnDetails = ({
  purchaseReturn,
  selectedInvoice,
  controlStyle,
  onPurchaseReturnChange,
  onInvoiceSelect
}) => {
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch next purchase return number when component mounts
  useEffect(() => {
    if (!purchaseReturn.returnNumber) {
      // Simplified: Just generate a fallback number if API doesn't have an endpoint for it yet
      // Or we can use purchaseReturnService if it has an endpoint. For now, use fallback.
      onPurchaseReturnChange(prev => ({
        ...prev,
        returnNumber: `PR-${Math.floor(10000 + Math.random() * 90000)}`
      }));
    }
  }, []);

  // Fetch invoice details when an invoice is selected
  useEffect(() => {
    if (selectedInvoice && (selectedInvoice.invoiceNo || selectedInvoice.purchaseNumber)) {
      const invoiceNo = selectedInvoice.invoiceNo || selectedInvoice.purchaseNumber;
      const updatedReturn = {
        ...purchaseReturn,
        originalInvoice: invoiceNo
      };
      
      onPurchaseReturnChange(updatedReturn);
    }
  }, [selectedInvoice]);

  const handleReasonChange = (e) => {
    const newReason = e.target.value;
    onPurchaseReturnChange(prev => ({
      ...prev,
      reasonForReturn: newReason
    }));
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 0.5, 
        mb: 0.5, 
        borderRadius: '4px',
        boxShadow: 'none',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.875rem' }}>
        Purchase Return Details
      </Typography>
      <Grid container spacing={0.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Ref Purchase Number"
            value={selectedInvoice ? selectedInvoice.purchaseNumber || selectedInvoice.invoiceNo || selectedInvoice.number || '' : ''}
            InputProps={{
              readOnly: true,
            }}
            required
            fullWidth
            margin="dense"
            size="small"
            sx={controlStyle}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Return Number"
            value={isLoading ? 'Loading...' : (purchaseReturn.returnNumber || '')}
            fullWidth
            margin="dense"
            size="small"
            InputProps={{
              readOnly: true,
            }}
            sx={controlStyle}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Ref Purchase Date"
              value={selectedInvoice ? 
                (selectedInvoice.issueDate ? new Date(selectedInvoice.issueDate) : 
                 selectedInvoice.date ? new Date(selectedInvoice.date) : null) : 
                null
              }
              readOnly
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  margin: "dense",
                  size: "small",
                  sx: controlStyle,
                  InputProps: { readOnly: true }
                } 
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={purchaseReturn.date}
              readOnly
              slotProps={{ 
                textField: { 
                  fullWidth: true, 
                  margin: "dense",
                  size: "small",
                  sx: controlStyle,
                  InputProps: { readOnly: true }
                } 
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Reason for Return"
            value={purchaseReturn.reasonForReturn || ''}
            onChange={handleReasonChange}
            fullWidth
            multiline
            rows={2.5}
            margin="dense"
            size="small"
            required
            error={!purchaseReturn.reasonForReturn}
            helperText={!purchaseReturn.reasonForReturn ? 'Reason is required' : ''}
            sx={{
              ...controlStyle,
              '& .MuiOutlinedInput-root': {
                minHeight: 90,
                borderRadius: 0
              }
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PurchaseReturnDetails;
